'use client';

import { useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Box } from '@mui/material';
import ImageLightbox from '@/components/publicSearch/ImageLightbox';
import { alpha } from '@mui/material/styles';
import { green } from '@/lib/colours';
import { SocietyFormattedDate } from '@/components/publicSearch/SocietyFormattedDate';
import { saleLotPriceLabel } from '@/hooks/publicSearch/sale-catalogue/saleLotPriceColumns';
import { buildSaleLotHref } from '@/lib/saleCataloguePaths';
import { useSaleIdParam } from '@/hooks/publicSearch/useSaleIdParam';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import { GetSaleLotsRow } from '../constants';
import { AnimalSexBadge } from '@/hooks/publicSearch/animal/helpers';
import { CardThumbnail } from './CardThumbnail';
import { pickPrimaryImageUrl } from '@/publicSearch/utils/pickPrimaryImageUrl';
import { useDocumentImageWithSociety } from '@/hooks/publicSearch/useDocumentImageWithSociety';
import { useCurrencySymbol } from '@/hooks/publicSearch/useCurrencySymbol';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { displayPlainText } from '@/utils/stripHtmlForTooltip';
import { sanitizeSaleLotIdent } from '@/hooks/publicSearch/sale-catalogue/resolveSaleLotDisplayName';
import {
  SaleCardCell,
  SaleCardGrid,
  SaleCardNameLink,
  SaleCardPropsHeader,
  SaleCardPropsValue,
  SaleStyledCard,
} from '@/components/publicSearch/sale-catalogues/sharedCardStyles';

const animalNameSx = {
  display: 'block',
  overflow: 'visible',
  WebkitLineClamp: 'unset',
  lineClamp: 'unset',
  textOverflow: 'clip',
  overflowWrap: 'anywhere',
  fontSize: '16px',
  lineHeight: 1.4,
} as const;

export const RegisteredBadgeStatus = ({
  status,
}: {
  status: string | null | undefined;
}) => {
  const { isDarkMode } = useResolvedColorScheme();
  const plain = displayPlainText(status);
  if (!plain) return null;
  return (
    <Box
      component="span"
      sx={{
        borderRadius: '4px',
        padding: '4px 12px',
        width: 'fit-content',
        fontSize: '12px',
        lineHeight: '16px',
        background: isDarkMode ? alpha(green[500], 0.2) : green[100],
        color: isDarkMode ? green[300] : green[700],
      }}
    >
      {plain}
    </Box>
  );
};

export default function SaleCatalogueDetailAnimalCard(props: GetSaleLotsRow) {
  const {
    ID: animalId,
    ANIMAL_IDENT: identifier,
    ANIMAL_NAME: rawName,
    REGISTRATION_STATUS: status,
    DATE_OF_BIRTH: birthDate,
    LOT_NO: lot,
    SEX: sex,
    documents,
    animalDocuments,
    lotNotes,
  } = props as GetSaleLotsRow & {
    animalDocuments?: Parameters<typeof pickPrimaryImageUrl>[1];
  };
  const name = displayPlainText(rawName);
  const safeIdent = sanitizeSaleLotIdent(identifier);
  const displayName = name || safeIdent || (lot ? `Lot ${lot}` : '—');

  const society = usePublicSearchContext((context) => context.society);
  const currencySymbol = useCurrencySymbol(society);
  const orgCode = society.societyCode ?? '';
  const societyId = society.societyId ?? (society as any).orgId ?? '';
  const searchParams = useSearchParams();
  const saleId = useSaleIdParam();

  const primaryImg = useMemo(
    () =>
      pickPrimaryImageUrl(
        documents as Parameters<typeof pickPrimaryImageUrl>[0],
        animalDocuments,
      ),
    [documents, animalDocuments],
  );

  const { imgSrc: displayImg } = useDocumentImageWithSociety(
    primaryImg,
    societyId,
  );
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);

  const buildPath = (): string => {
    if (!orgCode) return '';
    if (saleId && lot) {
      return buildSaleLotHref(orgCode, {
        saleId,
        lotNo: lot,
        animalId,
        searchParams,
      });
    }
    if (animalId && animalId > 0) {
      return `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${animalId}`;
    }
    return '';
  };
  const path = buildPath();

  const askingPriceLabel = saleLotPriceLabel(props, 'asking', currencySymbol);
  const salePriceLabel = saleLotPriceLabel(props, 'sale', currencySymbol);
  const lotNotesText = displayPlainText(lotNotes);

  return (
    <SaleStyledCard>
      <Box
        sx={{
          height: '24px',
          mb: '8px',
        }}
      >
        <RegisteredBadgeStatus status={status} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          mb: { xs: 1, sm: 0 },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {path ? (
            <SaleCardNameLink href={path} sx={animalNameSx}>
              {displayName}
            </SaleCardNameLink>
          ) : (
            <SaleCardPropsValue sx={{ ...animalNameSx, fontWeight: 600 }}>
              {displayName}
            </SaleCardPropsValue>
          )}
        </Box>
        <CardThumbnail
          displayImg={displayImg}
          primaryImg={primaryImg}
          onOpen={() => setIsLightboxOpen(true)}
        />
      </Box>
      <SaleCardGrid>
        {salePriceLabel && (
          <SaleCardCell sx={{ width: '100%' }}>
            <SaleCardPropsHeader>Sale Price</SaleCardPropsHeader>
            <SaleCardPropsValue sx={{ fontWeight: 600 }}>
              {salePriceLabel}
            </SaleCardPropsValue>
          </SaleCardCell>
        )}
        <SaleCardCell>
          <SaleCardPropsHeader>Sex</SaleCardPropsHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <AnimalSexBadge sex={sex ?? ''} />
          </Box>
        </SaleCardCell>
        <SaleCardCell>
          <SaleCardPropsHeader>Lot</SaleCardPropsHeader>
          <SaleCardPropsValue>{lot ?? '—'}</SaleCardPropsValue>
        </SaleCardCell>
        <SaleCardCell>
          <SaleCardPropsHeader>Identifier</SaleCardPropsHeader>
          <SaleCardPropsValue sx={{ wordBreak: 'break-all' }}>
            {safeIdent || '—'}
          </SaleCardPropsValue>
        </SaleCardCell>
        <SaleCardCell>
          <SaleCardPropsHeader>Birth Date</SaleCardPropsHeader>
          <SaleCardPropsValue>
            <SocietyFormattedDate value={birthDate} asDob />
          </SaleCardPropsValue>
        </SaleCardCell>
        {askingPriceLabel && (
          <SaleCardCell>
            <SaleCardPropsHeader>Asking Price</SaleCardPropsHeader>
            <SaleCardPropsValue>{askingPriceLabel}</SaleCardPropsValue>
          </SaleCardCell>
        )}
      </SaleCardGrid>
      {lotNotesText && (
        <Box sx={{ mt: 1 }}>
          <SaleCardPropsHeader>Lot Notes</SaleCardPropsHeader>
          <Box
            sx={{ fontSize: '14px', lineHeight: 1.5, overflowWrap: 'anywhere' }}
          >
            {lotNotesText}
          </Box>
        </Box>
      )}
      <ImageLightbox
        open={isLightboxOpen}
        src={displayImg}
        alt={displayName || 'Animal image'}
        title={displayName || undefined}
        onClose={() => setIsLightboxOpen(false)}
      />
    </SaleStyledCard>
  );
}
